import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { X, Flame, Meh, Frown, Smile, Star, ChevronDown } from 'lucide-react';
import FeedbackThankYou from './FeedbackThankYou';
import './SessionFeedback.css';

const TOTAL_QUESTIONS = 3;

export default function SessionFeedback({
  sessionId,
  apiBaseUrl,
  listeningTimeMinutes,
  effectiveMode = null,
  modesUsed = [],
  onClose,
  onRegenerated,
}) {
  const [wowMoment, setWowMoment] = useState(null);
  const [fightDj, setFightDj] = useState(null);
  const [vibeMatch, setVibeMatch] = useState(null);
  const [additionalText, setAdditionalText] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submitLock = useRef(false);

  const allAnswered = wowMoment !== null && fightDj !== null && vibeMatch !== null;
  const answeredCount = [wowMoment, fightDj, vibeMatch].filter((v) => v !== null).length;
  const progressPercent = (answeredCount / TOTAL_QUESTIONS) * 100;

  const handleSubmit = async () => {
    if (submitLock.current || !allAnswered) return;
    submitLock.current = true;
    setSubmitting(true);

    try {
      const userEmail = localStorage.getItem('user_email');
      const sfResp = await axios.post(`${apiBaseUrl}/submit-session-feedback`, {
        email: userEmail,
        sessionId,
        wowMoment,
        fightDj,
        vibeMatch,
        effectiveMode: effectiveMode,
        additionalText: additionalText.trim() || null,
        listeningTimeMinutes: listeningTimeMinutes || 0,
        modesUsed: modesUsed,
      });
      if (onRegenerated && sfResp?.data?.regeneratedPlaylistId) {
        onRegenerated(sfResp.data);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('[SessionFeedback] Error submitting:', err);
      toast.error('Failed to submit feedback. Please try again.', {
        autoClose: 3000,
        position: 'bottom-center',
      });
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  if (submitted) {
    return (
      <FeedbackThankYou
        listeningTimeMinutes={listeningTimeMinutes}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="session-feedback-overlay">
      <div className="session-feedback-card">
        <button className="sf-close-btn" onClick={onClose}>
          <X size={22} />
        </button>

        {/* Progress */}
        <div className="sf-progress">
          <div className="sf-progress-text">
            {answeredCount} of {TOTAL_QUESTIONS} answered
          </div>
          <div className="sf-progress-bar">
            <div
              className="sf-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="sf-questions">
          {/* Q1 – Wow moment */}
          <div className="sf-question-block">
            <h3>Did the music feel "wow" or make you feel "special"?</h3>
            <div className="sf-options sf-options--two">
              <button
                className={`sf-option-card ${wowMoment === false ? 'sf-option-card--selected' : ''}`}
                onClick={() => setWowMoment(false)}
              >
                <span className="sf-option-icon"><Meh size={28} /></span>
                <span>No</span>
              </button>
              <button
                className={`sf-option-card ${wowMoment === true ? 'sf-option-card--selected' : ''}`}
                onClick={() => setWowMoment(true)}
              >
                <span className="sf-option-icon"><Flame size={28} /></span>
                <span>Yes</span>
              </button>
            </div>
          </div>

          {/* Q2 – Fight the DJ */}
          <div className="sf-question-block">
            <h3>Did you feel the need to "fight" the DJ?</h3>
            <div className="sf-options sf-options--three">
              <button
                className={`sf-option-card ${fightDj === 'multiple_times' ? 'sf-option-card--selected' : ''}`}
                onClick={() => setFightDj('multiple_times')}
              >
                <span className="sf-option-icon"><Frown size={28} /></span>
                <span>Multiple times</span>
              </button>
              <button
                className={`sf-option-card ${fightDj === 'a_little' ? 'sf-option-card--selected' : ''}`}
                onClick={() => setFightDj('a_little')}
              >
                <span className="sf-option-icon"><Meh size={28} /></span>
                <span>A little</span>
              </button>
              <button
                className={`sf-option-card ${fightDj === 'not_at_all' ? 'sf-option-card--selected' : ''}`}
                onClick={() => setFightDj('not_at_all')}
              >
                <span className="sf-option-icon"><Smile size={28} /></span>
                <span>Not at all</span>
              </button>
            </div>
          </div>

          {/* Q3 – Vibe match */}
          <div className="sf-question-block">
            <h3>How well did the music match your vibe?</h3>
            <div className="sf-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`sf-star ${vibeMatch >= n ? 'sf-star--active' : ''}`}
                  onClick={() => setVibeMatch(n)}
                >
                  <Star size={36} fill={vibeMatch >= n ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
            <div className="sf-star-labels">
              <span>Very poorly</span>
              <span>Perfectly</span>
            </div>
          </div>

          {/* Additional text - collapsible */}
          <div className="sf-additional">
            <button
              className="sf-additional-toggle"
              onClick={() => setShowMore(!showMore)}
            >
              Want to tell us more?
              <span className={`sf-chevron ${showMore ? 'sf-chevron--open' : ''}`}>
                <ChevronDown size={16} />
              </span>
            </button>
            <div className={`sf-additional-content ${showMore ? 'sf-additional-content--open' : ''}`}>
              <textarea
                className="sf-textarea"
                placeholder="Share any thoughts about your session..."
                value={additionalText}
                onChange={(e) => setAdditionalText(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <button
            className="sf-submit-btn"
            onClick={handleSubmit}
            disabled={!allAnswered || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}
